

































import { Component, Prop, Vue, Watch, Inject } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import preloadImage from "@/lib/preload-image.function";
import ImageHandlerService from "@/modules/file-storage/image-handler.service";
import dataStore from "@/store";
import FileStorageStore from "@/modules/file-storage/store";

import FullscreenGallery from "./FullscreenGallery.vue";

import PlushieStore from "../store";
import PlushieImage from "../plushie-image.model";

@Component({
  components: {
    FullscreenGallery,
  },
})
export default class ArtworkGallery extends Vue {
  @Prop({ required: true })
  public readonly plushieId!: string;

  @Prop({ default: true })
  public readonly isReadOnly!: boolean;

  @Inject("ImageHandlerService")
  private fImageHandlerService!: ImageHandlerService;

  @Inject("window")
  private fWindow!: Window;

  private fActionErrors: string[] = [];

  private fIsLoading = true;
  private fIsDisabled = false;

  private fCurrentIndex = 0;

  private fFileStorageDataStore: FileStorageStore;
  private fPlushieDataStore: PlushieStore;

  get actionErrors(): string[] {
    return this.fActionErrors;
  }

  get artworks(): PlushieImage[] {
    return this.fPlushieDataStore.getPlushieImagesByPlushieId(this.plushieId);
  }

  get currentIndex(): number {
    return this.fCurrentIndex;
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get isDisabled(): boolean {
    return this.fIsDisabled;
  }

  get isTouchDevice(): boolean {
    return this.fWindow.matchMedia("(pointer: coarse)").matches;
  }

  get stageImage(): PlushieImage | undefined {
    if (this.currentIndex == null) {
      return undefined;
    }

    return this.artworks[this.currentIndex];
  }

  public constructor() {
    super();
    this.fFileStorageDataStore = getModule(FileStorageStore, dataStore);
    this.fPlushieDataStore = getModule(PlushieStore, dataStore);
  }

  public getStageThumbnail(artwork: PlushieImage): string {
    return this.getThumbnail(artwork, 600);
  }

  public getFullscreenThumbnail(artwork: PlushieImage): string {
    return this.getThumbnail(artwork, 1920);
  }

  public getThumbnail(
    artwork: PlushieImage,
    size: number,
    crop = false
  ): string {
    const storageItem = this.fFileStorageDataStore.getItemById(
      artwork.storageItem
    );

    const url = storageItem
      ? storageItem.url
      : this.fFileStorageDataStore.placeholderUrl;

    return this.fImageHandlerService.getThumbnailUrl(url, size, size, crop);
  }

  public launchGalleria(): void {
    const gallery = this.getFullscreenGallery();
    if (!gallery || this.currentIndex == null) {
      return;
    }

    const images = this.artworks.map((artwork) => {
      return {
        href: this.getFullscreenThumbnail(artwork),
        thumbnail: this.getThumbnail(artwork, 200, true),
      };
    });

    gallery.launchGalleria(images, this.currentIndex);
  }

  public setCurrentIndex(index: number): void {
    this.fCurrentIndex = index;
  }

  private getFullscreenGallery(): FullscreenGallery | undefined {
    return this.$refs["fullscreen-gallery"] as FullscreenGallery;
  }

  private async getPlushieData(plushieId: string) {
    const images = await this.fPlushieDataStore.loadPlushieImageByPlushieId({
      plushieId: plushieId,
    });

    const storageItemsIds = images.map((image) => image.storageItem);

    await this.fFileStorageDataStore.loadItemsByIds(storageItemsIds);

    if (this.artworks.length > 0) {
      this.setCurrentIndex(0);
    }

    void this.preloadImages();
  }

  private async preloadImages() {
    await Promise.all(
      this.artworks.map((artwork) =>
        preloadImage(this.getStageThumbnail(artwork))
      )
    );

    await Promise.all(
      this.artworks.map((artwork) =>
        preloadImage(this.getFullscreenThumbnail(artwork))
      )
    );
  }

  @Watch("artworks", { immediate: false })
  private async _onArtworksChange() {
    if (this.isLoading) {
      return;
    }

    await this.preloadImages();
  }

  @Watch("plushieId", { immediate: true })
  private async _onPlushieIdChange() {
    if (!this.plushieId) {
      return;
    }

    this.fIsLoading = true;

    await this.getPlushieData(this.plushieId);

    this.fIsLoading = false;
  }
}
